export function getLegacyExplanationEntries(image) {
  return Array.isArray(image?.explanations) ? image.explanations : [];
}

function normalizeVisualEvidenceItem(item) {
  if (!item) {
    return null;
  }

  if (typeof item === "string") {
    return {
      imageUrl: item,
      label: "",
      source: "",
      kind: "reference",
    };
  }

  const imageUrl = item.imageUrl || item.url || item.src || item.path || "";
  if (!imageUrl) {
    return null;
  }

  const bbox =
    item.bbox ||
    item.boundingBox ||
    item.box ||
    item.image_grounding_bbox ||
    null;
  const maskRle = item.maskRle || item.mask || item.rle || null;
  const maskSvgPath = item.maskSvgPath || item.svgMaskPath || item.segmentMask || null;
  const maskViewBox = item.maskViewBox || item.svgViewBox || item.segmentViewBox || null;

  return {
    imageUrl,
    label: item.label || item.title || item.caption || "",
    source: item.source || item.provider || item.origin || "",
    kind: item.kind || item.type || "reference",
    note: item.note || item.description || "",
    bbox,
    maskRle,
    maskSvgPath,
    maskViewBox,
    imageOrientation: item.imageOrientation ?? item.image_orientation ?? 1,
    conceptIndex: item.conceptIndex ?? item.concept_index ?? null,
    conceptName: item.conceptName || item.concept_name || item.matchedConcept || "",
    matchType: item.matchType || item.match_type || "",
    matchScore: item.matchScore ?? item.match_score ?? null,
    prediction: item.prediction || "",
    score: item.score ?? null,
    hideCaption: item.hideCaption === true,
  };
}

export function getVisualEvidenceItems(entry) {
  const rawItems =
    entry?.visualEvidence ||
    entry?.imageEvidence ||
    entry?.evidenceImages ||
    entry?.screenshots ||
    entry?.referenceImages ||
    [];

  return Array.isArray(rawItems)
    ? rawItems.map((item) => normalizeVisualEvidenceItem(item)).filter(Boolean)
    : [];
}

export function getExplanationEntryDedupKey(entry) {
  const evidenceKey = getVisualEvidenceItems(entry)
    .map((item) => `${item.imageUrl}|${item.label}|${item.source}|${item.kind}|${item.conceptName}|${item.matchType}`)
    .join("||");

  return [
    entry?.category || "",
    entry?.text || "",
    entry?.value || "",
    entry?.token_index ?? "",
    entry?.segment_id || "",
    evidenceKey,
  ].join("__");
}

export function getExplanationEntryIdentityKey(entry) {
  return [
    entry?.category || "",
    entry?.text || "",
    entry?.value || "",
    entry?.token_index ?? "",
    entry?.segment_id || "",
  ].join("__");
}

export function getTextualExplanationEntries(image) {
  if (Array.isArray(image?.factsExplanation) && image.factsExplanation.length > 0) {
    return image.factsExplanation;
  }
  return getLegacyExplanationEntries(image);
}

export function getVisualExplanationEntries(image) {
  if (Array.isArray(image?.visualExplanation) && image.visualExplanation.length > 0) {
    return image.visualExplanation;
  }
  return getLegacyExplanationEntries(image);
}

export function normalizeImageExplanationSchema(image) {
  if (!image || typeof image !== "object") {
    return image;
  }

  const legacy = getLegacyExplanationEntries(image);
  const next = { ...image };

  if (!Array.isArray(next.factsExplanation) || next.factsExplanation.length === 0) {
    next.factsExplanation = legacy.map((entry) => ({ ...entry }));
  }

  if (!Array.isArray(next.visualExplanation) || next.visualExplanation.length === 0) {
    next.visualExplanation = legacy.map((entry) => ({ ...entry }));
  }

  if (Array.isArray(next.visualExplanation)) {
    next.visualExplanation = next.visualExplanation.map((entry) => {
      const normalizedEvidence = getVisualEvidenceItems(entry);
      return normalizedEvidence.length > 0 && !Array.isArray(entry?.visualEvidence)
        ? { ...entry, visualEvidence: normalizedEvidence }
        : entry;
    });
  }

  delete next.explanations;
  return next;
}

export function normalizeDatasetExplanationSchema(dataset) {
  return Array.isArray(dataset)
    ? dataset.map((image) => normalizeImageExplanationSchema(image))
    : [];
}
